/**
 * Gate for the Kobikuul promo popup on the public homepage.
 *
 * Context (2026-09-04): MTN invited us to a programme to showcase the AI
 * chat, and the brochures handed out there carry a QR code that lands on the
 * mcare homepage — which has no visible Kobikuul entry point (the homepage
 * route is landing-pages/home-two; the older home.tsx with its banner is
 * commented out of the router). The popup is that bridge. It runs for THREE
 * DAYS from a visitor's first sight of it, then stops for good.
 *
 * Only an explicit dismissal hides it, and only for that browser session
 * (Ken, 2026-09-04): taking the CTA deliberately does NOT stop it, so someone
 * who tries the chat and comes back to the homepage is offered it again.
 *
 * Modelled on the profile-completion gate in ./profile-completion: same
 * timestamp-in-localStorage shape, same fail-open behaviour when storage is
 * unavailable (private browsing) — for a three-day campaign, a nudge missed
 * is worse than a nudge repeated.
 */

/** First time the popup was shown in this browser (ms since epoch). Starts
 *  the three-day clock. */
export const KOBIKUUL_PROMO_FIRST_SEEN_KEY = "@KobikuulPromoFirstSeen";

/** sessionStorage (not local): an explicit dismissal holds for the rest of
 *  this browser session only, so the popup still greets them on their next
 *  visit inside the window. */
export const KOBIKUUL_PROMO_DISMISSED_KEY = "@KobikuulPromoDismissed";

export const KOBIKUUL_PROMO_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * Link to the campaign chat, tagged with where the visitor came from.
 *
 * `source=pulse` is deliberate and load-bearing, not a placeholder:
 * pages/kobikuul sends away any arrival whose source is not pulse/prestige
 * (so a neutral source would bounce the visitor back to the homepage), and
 * only those two sources carry the coupon that makes the card's free-month
 * offer real (components/kobikuul/OnboardingCard). Ken's call, 2026-09-04.
 *
 * `campaign` is the separate tracking dimension, so each entry point stays
 * distinguishable from the SMS blast in the acquisition export. Every entry
 * point goes through here so the source rule can only be changed in one
 * place.
 */
export function kobikuulChatHref(campaign: string): string {
  return `/kobikuul?source=pulse&campaign=${encodeURIComponent(campaign)}`;
}

/** The homepage popup (brochure QR codes land there). */
export const KOBIKUUL_PROMO_DESTINATION = kobikuulChatHref("brochure");

/** Footer link on the public pages — the way back for anyone who dismissed
 *  the popup, or who arrives after the three-day window has closed. */
export const KOBIKUUL_FOOTER_DESTINATION = kobikuulChatHref("footer");

/** The games hub, reached from the deck's second card. Not a campaign link —
 *  no source or coupon is involved, it is just a page on the site. */
export const GAMES_HUB_PATH = "/play";

function readTimestamp(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    // storage unavailable — treat as "nothing recorded"
    return null;
  }
}

function readFlag(storage: Storage | undefined, key: string): boolean {
  try {
    return Boolean(storage?.getItem(key));
  } catch {
    // storage unavailable — a flag we cannot read is a flag that is not set
    return false;
  }
}

/** True when the popup should be shown on this homepage visit: not dismissed
 *  this session, and within three days of the first time it appeared. */
export function isKobikuulPromoDue(now: number = Date.now()): boolean {
  if (readFlag(window.sessionStorage, KOBIKUUL_PROMO_DISMISSED_KEY)) {
    return false;
  }

  const firstSeen = readTimestamp(KOBIKUUL_PROMO_FIRST_SEEN_KEY);
  // Never shown (or an unreadable/corrupt stamp) — show it and start the clock.
  if (firstSeen === null) return true;
  return now - firstSeen < KOBIKUUL_PROMO_WINDOW_MS;
}

/** Starts the three-day window on first sight. Idempotent, so a re-render (or
 *  StrictMode's double effect) cannot push the window forward. */
export function markKobikuulPromoShown(now: number = Date.now()): void {
  try {
    if (localStorage.getItem(KOBIKUUL_PROMO_FIRST_SEEN_KEY)) return;
    localStorage.setItem(KOBIKUUL_PROMO_FIRST_SEEN_KEY, String(now));
  } catch {
    // storage unavailable — the popup still shows, just on every visit
  }
}

export function markKobikuulPromoDismissed(): void {
  try {
    sessionStorage.setItem(KOBIKUUL_PROMO_DISMISSED_KEY, "1");
  } catch {
    // storage unavailable — nothing to remember it with
  }
}

/** Clears the gate so the popup behaves like a first-ever visit. For QA
 *  during the programme — nothing in the app calls this. */
export function resetKobikuulPromoGate(): void {
  try {
    localStorage.removeItem(KOBIKUUL_PROMO_FIRST_SEEN_KEY);
    sessionStorage.removeItem(KOBIKUUL_PROMO_DISMISSED_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
}
